import { create } from 'zustand';
import * as THREE from 'three';
import { GameState, SnakeState, FoodOrb, CometState, GAME_CONSTANTS, SNAKE_COLORS, QualityLevel, QUALITY_SETTINGS } from '@/types/game';
import { useDebugStore, DEBUG } from './useDebugEvents';
import { Analytics } from '@/lib/analytics';

const generateId = () => Math.random().toString(36).substr(2, 9);

// Regular food spawns within this zone (center-biased)
const FOOD_SPAWN_RADIUS = GAME_CONSTANTS.WORLD_SIZE / 2 * 0.8; // ±800

// Generate a center-biased random position for food spawning
const generateCenterBiasedPosition = (): THREE.Vector3 => {
  // For each axis, use triangular distribution (sum of 2 randoms) which peaks at center
  const biasedRandom = () => {
    const triangular = (Math.random() + Math.random()) / 2 - 0.5;
    return triangular * 2;
  };

  return new THREE.Vector3(
    biasedRandom() * FOOD_SPAWN_RADIUS,
    biasedRandom() * FOOD_SPAWN_RADIUS,
    biasedRandom() * FOOD_SPAWN_RADIUS
  );
};

const createSnake = (name: string, position: THREE.Vector3, isPlayer: boolean = false, initialMass?: number): SnakeState => {
  const color = SNAKE_COLORS[Math.floor(Math.random() * SNAKE_COLORS.length)];
  const segments: THREE.Vector3[] = [];
  
  // Use provided initial mass or default
  const segmentCount = initialMass || GAME_CONSTANTS.INITIAL_SEGMENTS;
  
  // Create initial segments behind the head
  for (let i = 0; i < segmentCount; i++) {
    segments.push(new THREE.Vector3(
      position.x,
      position.y,
      position.z - (i + 1) * GAME_CONSTANTS.SEGMENT_SPACING
    ));
  }

  return {
    id: isPlayer ? 'player' : generateId(),
    name,
    color,
    position: position.clone(),
    quaternion: new THREE.Quaternion(),
    velocity: GAME_CONSTANTS.BASE_SPEED,
    segments,
    turningPoints: [],
    score: segmentCount,
    reviveUsed: false,
    invincible: true,
    invincibilityTimer: GAME_CONSTANTS.INVINCIBILITY_DURATION,
    isBoosting: false,
    isAlive: true,
  };
};

const generateFood = (count: number): FoodOrb[] => {
  const food: FoodOrb[] = [];

  for (let i = 0; i < count; i++) {
    const color = SNAKE_COLORS[Math.floor(Math.random() * SNAKE_COLORS.length)];
    food.push({
      id: generateId(),
      position: generateCenterBiasedPosition(),
      value: GAME_CONSTANTS.FOOD_BASE_VALUE,
      color,
      radius: 1.6 + Math.random() * 0.8,
    });
  }

  return food;
};

// Generate a comet with random position and direction
const generateComet = (): CometState => {
  const halfWorld = GAME_CONSTANTS.WORLD_SIZE / 2;
  
  // Spawn at random edge of world
  const side = Math.floor(Math.random() * 6);
  const position = new THREE.Vector3();
  const direction = new THREE.Vector3();
  
  switch (side) {
    case 0: // +X face
      position.set(halfWorld * 0.9, (Math.random() - 0.5) * halfWorld, (Math.random() - 0.5) * halfWorld);
      direction.set(-1, (Math.random() - 0.5) * 0.3, (Math.random() - 0.5) * 0.3);
      break;
    case 1: // -X face
      position.set(-halfWorld * 0.9, (Math.random() - 0.5) * halfWorld, (Math.random() - 0.5) * halfWorld);
      direction.set(1, (Math.random() - 0.5) * 0.3, (Math.random() - 0.5) * 0.3);
      break;
    case 2: // +Y face
      position.set((Math.random() - 0.5) * halfWorld, halfWorld * 0.9, (Math.random() - 0.5) * halfWorld);
      direction.set((Math.random() - 0.5) * 0.3, -1, (Math.random() - 0.5) * 0.3);
      break;
    case 3: // -Y face
      position.set((Math.random() - 0.5) * halfWorld, -halfWorld * 0.9, (Math.random() - 0.5) * halfWorld);
      direction.set((Math.random() - 0.5) * 0.3, 1, (Math.random() - 0.5) * 0.3);
      break;
    case 4: // +Z face
      position.set((Math.random() - 0.5) * halfWorld, (Math.random() - 0.5) * halfWorld, halfWorld * 0.9);
      direction.set((Math.random() - 0.5) * 0.3, (Math.random() - 0.5) * 0.3, -1);
      break;
    default: // -Z face
      position.set((Math.random() - 0.5) * halfWorld, (Math.random() - 0.5) * halfWorld, -halfWorld * 0.9);
      direction.set((Math.random() - 0.5) * 0.3, (Math.random() - 0.5) * 0.3, 1);
  }
  
  direction.normalize();
  
  return {
    id: generateId(),
    position,
    direction,
    velocity: GAME_CONSTANTS.COMET_VELOCITY,
    createdAt: Date.now(),
  };
};

const generateAISnakes = (count: number, excludePosition: THREE.Vector3): SnakeState[] => {
  const snakes: SnakeState[] = [];
  const names = ['Nebula', 'Cosmic', 'Stardust', 'Void', 'Eclipse', 'Nova', 'Pulsar', 'Quasar', 'Zenith', 'Astro',
                 'Comet', 'Meteor', 'Galaxy', 'Orbit', 'Lunar', 'Solar', 'Titan', 'Vortex', 'Plasma', 'Photon'];
  
  for (let i = 0; i < count; i++) {
    const angle = (i / count) * Math.PI * 2;
    const distance = 200 + Math.random() * 300;
    const position = new THREE.Vector3(
      Math.cos(angle) * distance,
      (Math.random() - 0.5) * 400,
      Math.sin(angle) * distance
    );
    
    // Vary starting mass: 20% small (5), 50% medium (10-20), 30% large (25-50)
    let initialMass: number;
    const roll = Math.random();
    if (roll < 0.2) {
      initialMass = 5; // Small snakes
    } else if (roll < 0.7) {
      initialMass = 10 + Math.floor(Math.random() * 11); // Medium: 10-20
    } else {
      initialMass = 25 + Math.floor(Math.random() * 26); // Large: 25-50
    }
    
    snakes.push(createSnake(names[i % names.length], position, false, initialMass));
  }
  
  return snakes;
};

interface VirtualDirections {
  up: boolean;
  down: boolean;
  left: boolean;
  right: boolean;
}

interface GameStore extends GameState {
  virtualDirections: VirtualDirections;
  lastCometSpawn: number;
  nextCometInterval: number; // Random interval for next comet spawn
  quality: QualityLevel;
  startGame: (playerName: string) => void;
  endGame: () => void;
  respawn: () => void;
  revive: () => void;
  setMuted: (muted: boolean) => void;
  setQuality: (quality: QualityLevel) => void;
  setBoosting: (boosting: boolean) => void;
  setVirtualDirections: (directions: VirtualDirections) => void;
  updatePlayerPosition: (position: THREE.Vector3, quaternion: THREE.Quaternion) => void;
  eatFood: (foodId: string) => void;
  aiEatFood: (snakeId: string, foodId: string) => void;
  eatComet: (cometId: string, catcherName: string) => void;
  updateComets: (delta: number) => void;
  clearCometCatcher: () => void;
  killSnake: (snakeId: string, killerType?: 'ai' | 'boundary' | 'self') => void;
  updateAISnakes: (delta: number, time: number) => void;
  updateInvincibility: (delta: number) => void;
  addSegment: () => void;
  addSegments: (count: number) => void;
  removeSegment: () => void;
  spawnFood: (position: THREE.Vector3, count: number) => void;
}

// LocalStorage key for mute persistence
const MUTE_STORAGE_KEY = 'spacasnake_muted';
const QUALITY_STORAGE_KEY = 'spacasnake_quality';

// Detect if running on mobile device
const isMobile = (): boolean => {
  if (typeof window === 'undefined') return false;
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
};

// Load mute state from localStorage
const loadMutedState = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    const stored = localStorage.getItem(MUTE_STORAGE_KEY);
    return stored === 'true';
  } catch {
    return false;
  }
};

// Save mute state to localStorage
const saveMutedState = (muted: boolean): void => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(MUTE_STORAGE_KEY, String(muted));
  } catch {
    // Ignore storage errors
  }
};

// Load quality setting from localStorage (auto-detect mobile)
const loadQuality = (): QualityLevel => {
  if (typeof window === 'undefined') return 'high';
  try {
    const stored = localStorage.getItem(QUALITY_STORAGE_KEY);
    if (stored === 'high' || stored === 'low') return stored;
    return isMobile() ? 'low' : 'high';
  } catch {
    return isMobile() ? 'low' : 'high';
  }
};

// Save quality setting to localStorage
const saveQuality = (quality: QualityLevel): void => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(QUALITY_STORAGE_KEY, quality);
  } catch {
    // Ignore storage errors
  }
};

export const useGameStore = create<GameStore>((set, get) => ({
  playerSnake: null,
  aiSnakes: [],
  food: [],
  comets: [],
  cometCatcher: null,
  isPlaying: false,
  isPaused: false,
  isMuted: loadMutedState(),
  quality: loadQuality(),
  virtualDirections: { up: false, down: false, left: false, right: false },
  lastCometSpawn: 0,
  nextCometInterval: GAME_CONSTANTS.COMET_SPAWN_INTERVAL_MIN,

  startGame: (playerName) => {
    const { quality } = get();
    const settings = QUALITY_SETTINGS[quality];

    const playerPosition = new THREE.Vector3(0, 0, 0);
    const playerSnake = createSnake(playerName, playerPosition, true);
    const aiSnakes = generateAISnakes(settings.aiCount, playerPosition);
    const food = generateFood(settings.foodCount);

    // Start with one comet
    const comets = [generateComet()];

    // Generate random interval for first comet spawn
    const firstInterval = GAME_CONSTANTS.COMET_SPAWN_INTERVAL_MIN +
      Math.random() * (GAME_CONSTANTS.COMET_SPAWN_INTERVAL_MAX - GAME_CONSTANTS.COMET_SPAWN_INTERVAL_MIN);

    set({
      playerSnake,
      aiSnakes,
      food,
      comets,
      cometCatcher: null,
      lastCometSpawn: Date.now(),
      nextCometInterval: firstInterval,
      isPlaying: true,
      isPaused: false,
    });
  },

  endGame: () => {
    set({
      isPlaying: false,
    });
  },

  respawn: () => {
    const playerPosition = new THREE.Vector3(
      (Math.random() - 0.5) * 400,
      (Math.random() - 0.5) * 400,
      (Math.random() - 0.5) * 400
    );
    const playerSnake = createSnake(get().playerSnake?.name || 'Player', playerPosition, true);
    
    set({
      playerSnake,
      isPlaying: true,
    });
  },

  revive: () => {
    const { playerSnake } = get();
    if (!playerSnake || playerSnake.reviveUsed) return;

    const halfScore = Math.floor(playerSnake.score / 2);
    const segments: THREE.Vector3[] = [];
    
    for (let i = 0; i < halfScore; i++) {
      segments.push(new THREE.Vector3(
        playerSnake.position.x,
        playerSnake.position.y,
        playerSnake.position.z - (i + 1) * GAME_CONSTANTS.SEGMENT_SPACING
      ));
    }

    set({
      playerSnake: {
        ...playerSnake,
        segments,
        score: halfScore,
        reviveUsed: true,
        isAlive: true,
        invincible: true,
        invincibilityTimer: GAME_CONSTANTS.INVINCIBILITY_DURATION,
      },
      isPlaying: true,
    });
  },

  setMuted: (muted) => {
    saveMutedState(muted);
    set({ isMuted: muted });
  },

  setQuality: (quality) => {
    saveQuality(quality);
    set({ quality });
  },

  setVirtualDirections: (directions) => set({ virtualDirections: directions }),

  setBoosting: (boosting) => {
    const { playerSnake } = get();
    if (!playerSnake) return;

    // Track boost activation (only when starting to boost)
    if (boosting && !playerSnake.isBoosting) {
      Analytics.boostUsed(playerSnake.score);
    }

    set({
      playerSnake: {
        ...playerSnake,
        isBoosting: boosting,
        velocity: boosting ? GAME_CONSTANTS.BOOST_SPEED : GAME_CONSTANTS.BASE_SPEED,
      },
    });
  },

  updatePlayerPosition: (position, quaternion) => {
    const { playerSnake } = get();
    if (!playerSnake) return;

    set({
      playerSnake: {
        ...playerSnake,
        position: position.clone(),
        quaternion: quaternion.clone(),
      },
    });
  },

  eatFood: (foodId) => {
    const { food, playerSnake } = get();
    const eatenFood = food.find(f => f.id === foodId);
    if (!eatenFood || !playerSnake) return;

    const newFood = food.filter(f => f.id !== foodId);

    // Spawn replacement food with center bias
    newFood.push({
      id: generateId(),
      position: generateCenterBiasedPosition(),
      value: GAME_CONSTANTS.FOOD_BASE_VALUE,
      color: SNAKE_COLORS[Math.floor(Math.random() * SNAKE_COLORS.length)],
      radius: 1.6 + Math.random() * 0.8,
    });

    set({
      food: newFood,
      playerSnake: {
        ...playerSnake,
        score: playerSnake.score + eatenFood.value,
      },
    });
  },

  aiEatFood: (snakeId, foodId) => {
    const { food, aiSnakes } = get();
    const eatenFood = food.find(f => f.id === foodId);
    const aiSnake = aiSnakes.find(s => s.id === snakeId);
    if (!eatenFood || !aiSnake) return;

    const newFood = food.filter(f => f.id !== foodId);

    // Spawn replacement food with center bias
    newFood.push({
      id: generateId(),
      position: generateCenterBiasedPosition(),
      value: GAME_CONSTANTS.FOOD_BASE_VALUE,
      color: SNAKE_COLORS[Math.floor(Math.random() * SNAKE_COLORS.length)],
      radius: 1.6 + Math.random() * 0.8,
    });

    // Update AI snake score and add segment
    const newSegment = aiSnake.segments[aiSnake.segments.length - 1]?.clone() || aiSnake.position.clone();
    
    const updatedAiSnakes = aiSnakes.map(s => 
      s.id === snakeId 
        ? { 
            ...s, 
            score: s.score + eatenFood.value,
            segments: [...s.segments, newSegment],
            targetFoodId: undefined // Clear target since food was eaten
          }
        : s
    );

    set({
      food: newFood,
      aiSnakes: updatedAiSnakes,
    });
  },

  killSnake: (snakeId, killerType) => {
    const { playerSnake, aiSnakes } = get();

    if (snakeId === 'player' && playerSnake) {
      // Track player death with analytics
      if (killerType) {
        Analytics.gameDeath(playerSnake.score, killerType);
      }

      set({
        playerSnake: { ...playerSnake, isAlive: false },
        isPlaying: false,
      });
    } else {
      const deadSnake = aiSnakes.find(s => s.id === snakeId);
      if (deadSnake) {
        // Spawn food from dead snake
        const { food } = get();
        const newFood = [...food];
        const foodCount = Math.min(deadSnake.score, 20);
        
        for (let i = 0; i < foodCount; i++) {
          // Spawn food near death location (temporary - snakes will return to center after eating)
          newFood.push({
            id: generateId(),
            position: new THREE.Vector3(
              deadSnake.position.x + (Math.random() - 0.5) * 20,
              deadSnake.position.y + (Math.random() - 0.5) * 20,
              deadSnake.position.z + (Math.random() - 0.5) * 20
            ),
            value: 3,
            color: deadSnake.color,
            radius: 2.4,
          });
        }

        // Respawn AI snake elsewhere
        const angle = Math.random() * Math.PI * 2;
        const distance = 300 + Math.random() * 400;
        const newPosition = new THREE.Vector3(
          Math.cos(angle) * distance,
          (Math.random() - 0.5) * 400,
          Math.sin(angle) * distance
        );

        const newAISnakes = aiSnakes.map(s => 
          s.id === snakeId 
            ? createSnake(s.name, newPosition, false)
            : s
        );

        set({ aiSnakes: newAISnakes, food: newFood });
      }
    }
  },

  updateAISnakes: (delta, time) => {
    const { aiSnakes, playerSnake, food } = get();
    if (!playerSnake || !playerSnake.isAlive) return;

    // Create a set of existing food IDs for quick lookup
    const foodIds = new Set(food.map(f => f.id));

    // Track food eaten by AI snakes this frame
    const eatenFoodIds = new Set<string>();

    // Track AI eating events for debug
    const aiEatData: { snakeId: string; snakeName: string; beforeScore: number; afterScore: number }[] = [];

    const updatedSnakes = aiSnakes.map(snake => {
      if (!snake.isAlive) return snake;

      let newTargetFoodId = snake.targetFoodId;
      let newWanderUntil = snake.wanderUntil;
      const halfWorld = GAME_CONSTANTS.WORLD_SIZE / 2 * 0.95; // 950 - wall limit
      const dangerZone = FOOD_SPAWN_RADIUS; // 600 - regular food spawns within this

      // ============================================
      // STEP 1: CHECK WANDERING STATE
      // ============================================
      const now = Date.now();
      const isWandering = newWanderUntil && now < newWanderUntil;

      // Calculate direction based on wandering or food targeting
      let foodDir = new THREE.Vector3();

      if (isWandering) {
        // Wandering mode - continue forward with slight random drift
        // Get current forward direction
        const currentForward = new THREE.Vector3(0, 0, 1).applyQuaternion(snake.quaternion);

        // Add gentle random drift (small random adjustments)
        const driftStrength = 0.1;
        const drift = new THREE.Vector3(
          (Math.random() - 0.5) * driftStrength,
          (Math.random() - 0.5) * driftStrength,
          (Math.random() - 0.5) * driftStrength
        );

        foodDir.copy(currentForward).add(drift).normalize();
        newTargetFoodId = undefined; // Don't target food while wandering
      } else {
        // Clear wanderUntil if it has expired
        if (newWanderUntil) {
          newWanderUntil = undefined;
        }

        // ============================================
        // STEP 1B: FIND VALID FOOD TARGET (normal mode)
        // ============================================
        // Key rule: Don't target food that requires moving toward a wall
        // when already near that wall.

        const isValidFoodTarget = (f: FoodOrb): boolean => {
          // Check each axis: if snake is in danger zone on that axis,
          // don't target food that's even further in that direction
          if (snake.position.x > dangerZone && f.position.x > snake.position.x) return false;
          if (snake.position.x < -dangerZone && f.position.x < snake.position.x) return false;
          if (snake.position.y > dangerZone && f.position.y > snake.position.y) return false;
          if (snake.position.y < -dangerZone && f.position.y < snake.position.y) return false;
          if (snake.position.z > dangerZone && f.position.z > snake.position.z) return false;
          if (snake.position.z < -dangerZone && f.position.z < snake.position.z) return false;
          return true;
        };

        let targetFood: FoodOrb | undefined;

        // Check if current target still exists, not eaten, AND still valid direction
        if (snake.targetFoodId && foodIds.has(snake.targetFoodId) && !eatenFoodIds.has(snake.targetFoodId)) {
          const currentTarget = food.find(f => f.id === snake.targetFoodId);
          if (currentTarget && isValidFoodTarget(currentTarget)) {
            targetFood = currentTarget;
          }
          // If current target is no longer valid (would lead toward wall), clear it
        }

        // Find new target if needed
        if (!targetFood) {
          let bestFood: FoodOrb | undefined;
          let bestDist = Infinity;

          for (const f of food) {
            if (eatenFoodIds.has(f.id)) continue;
            if (!isValidFoodTarget(f)) continue; // Skip food toward walls

            const dist = snake.position.distanceTo(f.position);
            if (dist < bestDist) {
              bestDist = dist;
              bestFood = f;
            }
          }

          // If no valid food found (all food is toward walls), target center
          targetFood = bestFood;
          newTargetFoodId = bestFood?.id;
        }

        // Calculate direction to food (or center if no valid food)
        if (targetFood) {
          foodDir.subVectors(targetFood.position, snake.position).normalize();
        } else {
          // No valid food - head toward center
          foodDir.set(-snake.position.x, -snake.position.y, -snake.position.z).normalize();
        }
      }

      // ============================================
      // STEP 2: WALL AVOIDANCE
      // ============================================
      // Calculate repulsion from walls - stronger as snake gets closer
      const wallAvoidance = new THREE.Vector3(0, 0, 0);
      const avoidanceStart = 600; // Start avoiding at ±600

      const calcAvoidance = (pos: number, axis: 'x' | 'y' | 'z') => {
        const absPos = Math.abs(pos);
        if (absPos > avoidanceStart) {
          // Strength: 0 at avoidanceStart, 1 at halfWorld (950)
          const t = (absPos - avoidanceStart) / (halfWorld - avoidanceStart);
          // Exponential curve for strong avoidance near walls
          const strength = t * t * t; // Cubic - very strong near walls
          wallAvoidance[axis] = -Math.sign(pos) * strength;
        }
      };

      calcAvoidance(snake.position.x, 'x');
      calcAvoidance(snake.position.y, 'y');
      calcAvoidance(snake.position.z, 'z');

      // Blend food direction with wall avoidance
      let targetDir = new THREE.Vector3();
      const avoidanceStrength = wallAvoidance.length();

      if (avoidanceStrength > 0.01) {
        const avoidanceDir = wallAvoidance.clone().normalize();
        // Strong blending - wall avoidance dominates near walls
        const blendFactor = Math.min(avoidanceStrength * 1.5, 0.95);
        targetDir.copy(foodDir).lerp(avoidanceDir, blendFactor).normalize();
      } else {
        targetDir.copy(foodDir);
      }

      // ============================================
      // STEP 3: APPLY TURNING
      // ============================================
      // Faster turning when avoiding walls
      const turnRate = avoidanceStrength > 0.01
        ? 0.25 + avoidanceStrength * 0.35
        : 0.15;

      const targetQuat = new THREE.Quaternion();
      // lookAt: make +Z point toward target (swap eye/target to flip from camera convention)
      const lookMatrix = new THREE.Matrix4().lookAt(
        snake.position.clone().add(targetDir),  // "eye" = target position
        snake.position,                          // "target" = snake position
        new THREE.Vector3(0, 1, 0)
      );
      targetQuat.setFromRotationMatrix(lookMatrix);
      const newQuat = snake.quaternion.clone().slerp(targetQuat, turnRate);

      // ============================================
      // STEP 3: MOVE FORWARD
      // ============================================
      const forward = new THREE.Vector3(0, 0, 1).applyQuaternion(newQuat);
      const newPos = snake.position.clone().add(forward.multiplyScalar(snake.velocity * delta));

      // Boundary clamp
      newPos.x = Math.max(-halfWorld, Math.min(halfWorld, newPos.x));
      newPos.y = Math.max(-halfWorld, Math.min(halfWorld, newPos.y));
      newPos.z = Math.max(-halfWorld, Math.min(halfWorld, newPos.z));

      // Update segments (follow the head)
      const newSegments = [snake.position.clone(), ...snake.segments.slice(0, -1)];

      // Check for food collision at new position
      const aiGrowthFactor = 1 + Math.log10(1 + snake.score * 0.1) * 0.4;
      const aiHeadRadius = GAME_CONSTANTS.SNAKE_BASE_RADIUS * aiGrowthFactor;
      const aiEatMultiplier = 3.0;

      let scoreIncrease = 0;
      let extraSegments: THREE.Vector3[] = [];

      for (const orb of food) {
        if (eatenFoodIds.has(orb.id)) continue;

        const dist = newPos.distanceTo(orb.position);
        if (dist < (aiHeadRadius + orb.radius) * aiEatMultiplier) {
          eatenFoodIds.add(orb.id);
          scoreIncrease += orb.value;
          extraSegments.push(newSegments[newSegments.length - 1]?.clone() || newPos.clone());
          newTargetFoodId = undefined; // Clear target since we ate it
          // Start wandering for 3-7 seconds after eating
          newWanderUntil = Date.now() + 3000 + Math.random() * 4000;
          break;
        }
      }

      // Track for debug event
      if (DEBUG && scoreIncrease > 0) {
        aiEatData.push({
          snakeId: snake.id,
          snakeName: snake.name,
          beforeScore: snake.score,
          afterScore: snake.score + scoreIncrease,
        });
      }

      return {
        ...snake,
        position: newPos,
        quaternion: newQuat,
        segments: [...newSegments, ...extraSegments],
        score: snake.score + scoreIncrease,
        isBoosting: false,
        targetFoodId: newTargetFoodId,
        wanderUntil: newWanderUntil,
      };
    });

    // Update food array - remove eaten food and spawn replacements
    let newFood = food;
    if (eatenFoodIds.size > 0) {
      newFood = food.filter(f => !eatenFoodIds.has(f.id));
      
      // Spawn replacement food for each eaten (center-biased)
      for (let i = 0; i < eatenFoodIds.size; i++) {
        newFood.push({
          id: generateId(),
          position: generateCenterBiasedPosition(),
          value: GAME_CONSTANTS.FOOD_BASE_VALUE,
          color: SNAKE_COLORS[Math.floor(Math.random() * SNAKE_COLORS.length)],
          radius: 1.6 + Math.random() * 0.8,
        });
      }
    }

    set({ aiSnakes: updatedSnakes, food: newFood });
    
    // Emit debug events with rank calculation
    if (DEBUG && aiEatData.length > 0) {
      // Calculate ranks based on updated scores
      const allSnakes = [...updatedSnakes];
      if (playerSnake) allSnakes.push(playerSnake);
      
      // Sort by score descending to get ranks
      const sortedBefore = [...allSnakes].sort((a, b) => b.score - a.score);
      const sortedAfter = [...allSnakes].map(s => {
        const eatEvent = aiEatData.find(e => e.snakeId === s.id);
        return { ...s, score: eatEvent ? eatEvent.afterScore : s.score };
      }).sort((a, b) => b.score - a.score);
      
      for (const data of aiEatData) {
        const beforeRank = sortedBefore.findIndex(s => s.id === data.snakeId) + 1;
        const afterRank = sortedAfter.findIndex(s => s.id === data.snakeId) + 1;
        
        useDebugStore.getState().addAIEatEvent({
          snakeName: data.snakeName,
          beforeScore: data.beforeScore,
          afterScore: data.afterScore,
          beforeRank,
          afterRank,
        });
      }
    }
  },

  addSegment: () => {
    const { playerSnake } = get();
    if (!playerSnake) return;

    const lastSegment = playerSnake.segments[playerSnake.segments.length - 1];
    const newSegment = lastSegment.clone();
    const newScore = playerSnake.score + 1;

    set({
      playerSnake: {
        ...playerSnake,
        segments: [...playerSnake.segments, newSegment],
        score: newScore,
      },
    });

    // Track score milestones
    Analytics.checkScoreMilestone(newScore);
  },

  removeSegment: () => {
    const { playerSnake } = get();
    if (!playerSnake || playerSnake.segments.length <= 1) {
      // Auto-stop boosting when at minimum mass
      if (playerSnake?.isBoosting) {
        set({
          playerSnake: {
            ...playerSnake,
            isBoosting: false,
            velocity: GAME_CONSTANTS.BASE_SPEED,
          },
        });
      }
      return;
    }

    const newSegments = playerSnake.segments.slice(0, -1);
    const newScore = Math.max(1, playerSnake.score - 1);
    
    // Stop boosting if we're down to 1 segment
    const shouldStopBoosting = newSegments.length <= 1;

    set({
      playerSnake: {
        ...playerSnake,
        segments: newSegments,
        score: newScore,
        isBoosting: shouldStopBoosting ? false : playerSnake.isBoosting,
        velocity: shouldStopBoosting ? GAME_CONSTANTS.BASE_SPEED : playerSnake.velocity,
      },
    });
  },

  spawnFood: (position, count) => {
    const { food } = get();
    const newFood = [...food];

    for (let i = 0; i < count; i++) {
      newFood.push({
        id: generateId(),
        position: new THREE.Vector3(
          position.x + (Math.random() - 0.5) * 30,
          position.y + (Math.random() - 0.5) * 30,
          position.z + (Math.random() - 0.5) * 30
        ),
        value: 3,
        color: SNAKE_COLORS[Math.floor(Math.random() * SNAKE_COLORS.length)],
        radius: 2.4,
      });
    }

    set({ food: newFood });
  },

  // Comet functions
  eatComet: (cometId, catcherName) => {
    const { comets, playerSnake } = get();
    const newComets = comets.filter(c => c.id !== cometId);
    
    set({
      comets: newComets,
      cometCatcher: catcherName,
    });
  },

  updateComets: (delta) => {
    const { comets, lastCometSpawn, isPlaying } = get();
    if (!isPlaying) return;
    
    const now = Date.now();
    const halfWorld = GAME_CONSTANTS.WORLD_SIZE / 2;
    
    // Update comet positions
    const updatedComets = comets
      .map(comet => {
        const movement = comet.direction.clone().multiplyScalar(comet.velocity * delta);
        const newPosition = comet.position.clone().add(movement);
        
        return {
          ...comet,
          position: newPosition,
        };
      })
      // Remove comets that have left the world
      .filter(comet => {
        const { x, y, z } = comet.position;
        return Math.abs(x) < halfWorld && Math.abs(y) < halfWorld && Math.abs(z) < halfWorld;
      });
    
    // Spawn new comets periodically (random interval between 1-3 minutes)
    let newComets = updatedComets;
    const { nextCometInterval } = get();
    if (now - lastCometSpawn > nextCometInterval && 
        newComets.length < GAME_CONSTANTS.COMET_MAX_COUNT) {
      newComets = [...newComets, generateComet()];
      // Generate new random interval for next spawn
      const newInterval = GAME_CONSTANTS.COMET_SPAWN_INTERVAL_MIN + 
        Math.random() * (GAME_CONSTANTS.COMET_SPAWN_INTERVAL_MAX - GAME_CONSTANTS.COMET_SPAWN_INTERVAL_MIN);
      set({ lastCometSpawn: now, nextCometInterval: newInterval });
    }
    
    set({ comets: newComets });
  },

  clearCometCatcher: () => {
    set({ cometCatcher: null });
  },

  updateInvincibility: (delta) => {
    const { playerSnake, aiSnakes } = get();
    const deltaMs = delta * 1000;
    
    let updatedPlayerSnake = playerSnake;
    
    // Update player invincibility
    if (playerSnake && playerSnake.invincible) {
      const newTimer = playerSnake.invincibilityTimer - deltaMs;
      
      if (newTimer <= 0) {
        updatedPlayerSnake = {
          ...playerSnake,
          invincible: false,
          invincibilityTimer: 0,
        };
      } else {
        updatedPlayerSnake = {
          ...playerSnake,
          invincibilityTimer: newTimer,
        };
      }
    }
    
    // Update AI snakes invincibility
    let aiSnakesUpdated = false;
    const updatedAiSnakes = aiSnakes.map(snake => {
      if (!snake.invincible) return snake;
      
      aiSnakesUpdated = true;
      const newTimer = snake.invincibilityTimer - deltaMs;
      
      if (newTimer <= 0) {
        return {
          ...snake,
          invincible: false,
          invincibilityTimer: 0,
        };
      } else {
        return {
          ...snake,
          invincibilityTimer: newTimer,
        };
      }
    });
    
    // Only update state if something changed
    if (updatedPlayerSnake !== playerSnake || aiSnakesUpdated) {
      set({
        playerSnake: updatedPlayerSnake,
        aiSnakes: updatedAiSnakes,
      });
    }
  },

  addSegments: (count) => {
    const { playerSnake } = get();
    if (!playerSnake) return;

    const newSegments = [...playerSnake.segments];
    const lastSegment = newSegments[newSegments.length - 1];

    for (let i = 0; i < count; i++) {
      newSegments.push(lastSegment.clone());
    }

    const newScore = playerSnake.score + count;

    set({
      playerSnake: {
        ...playerSnake,
        segments: newSegments,
        score: newScore,
      },
    });

    // Track score milestones
    Analytics.checkScoreMilestone(newScore);
  },
}));
